import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import {
  deleteBusinessWorkflow,
  getBrowserTimezone,
  getBusinessWorkflows,
  getRunInputMessageContent,
  getRunInputMessages,
  getBusinessWorkflowRuns,
  getGmailStatus,
  getWorkflowSyncStatus,
  syncWorkflowNow,
  updateBusinessWorkflow,
  updateGmailSettings,
} from "../businessModeService";
import BusinessWorkflowActionItemsSummary from "./BusinessWorkflowActionItemsSummary";
import BusinessWorkflowRunHistory from "./BusinessWorkflowRunHistory";
import BusinessPageLoader from "./shared/BusinessPageLoader";
import GmailFilterSetupCard from "./shared/GmailFilterSetupCard";
import {
  buildSettingsGmailFiltersPayload,
  MEETING_DEFAULT_KEYWORDS,
  MEETING_DEFAULT_SENDERS,
  parseFiltersPayload,
} from "./shared/businessFilterPayload";
import {
  getWorkflowLifecycleStatus,
  shouldAutoRefreshActionItemsForFirstRun,
} from "./shared/workflowStatus";
/**
 * Convert backend run-history errors into user-friendly copy.
 *
 * @example
 * // Input
 * getRunHistoryErrorMessage(new Error("404 Not found: Table project.dataset.table was not found"));
 * // Output
 * "Please wait for at least one workflow run to finish. Run history becomes available after the first digest is generated."
 */
const getRunHistoryErrorMessage = (error) => {
  const rawMessage = String(error?.message || "");
  const normalized = rawMessage.toLowerCase();
  const tableNotReady =
    normalized.includes("not found: table") ||
    (normalized.includes("404") && normalized.includes("table") && normalized.includes("not found"));

  if (tableNotReady) {
    return FIRST_RUN_HISTORY_PENDING_MESSAGE;
  }
  return rawMessage || "Failed to fetch workflow run history.";
};

/**
 * Convert backend input-message errors into user-friendly copy.
 *
 * @example
 * // Input
 * getInputMessagesErrorMessage(new Error("400 digest_payload_gcs_url is required"));
 * // Output
 * "Run selection is missing payload metadata. Please choose another run."
 */
const getInputMessagesErrorMessage = (error) => {
  const rawMessage = String(error?.message || "");
  const normalized = rawMessage.toLowerCase();
  if (normalized.includes("digest_payload_gcs_url")) {
    return "Run selection is missing payload metadata. Please choose another run.";
  }
  return rawMessage || "Failed to fetch source input messages.";
};

/**
 * Build a stable unique key for one run record.
 *
 * @example
 * // Input
 * buildRunSelectionKey({ digest_date: "2026-04-22", created_at: "2026-04-22T11:00:00Z", digest_payload_gcs_url: "gs://bucket/a.json" });
 * // Output
 * "2026-04-22-2026-04-22T11:00:00Z-gs://bucket/a.json"
 */
const buildRunSelectionKey = (run) => {
  if (!run) {
    return "";
  }
  return `${String(run.digest_date || "")}-${String(run.created_at || "")}-${String(
    run.digest_payload_gcs_url || ""
  )}`;
};

const FIRST_RUN_HISTORY_PENDING_MESSAGE =
  "Please wait for at least one workflow run to finish. Run history becomes available after the first digest is generated.";
const SUPPORTED_SETTINGS_TABS = new Set(["todo", "settings"]);
const WORKFLOW_STATUS_REFRESH_INTERVAL_MS = 5000;

const BusinessWorkflowSettings = () => {
  const { workflowId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("todo");
  const [workflow, setWorkflow] = useState(null);
  const [meetingSenderPatterns, setMeetingSenderPatterns] = useState(MEETING_DEFAULT_SENDERS);
  const [meetingSubjectKeywords, setMeetingSubjectKeywords] = useState(MEETING_DEFAULT_KEYWORDS);
  const [workflowName, setWorkflowName] = useState("");
  const [runHistory, setRunHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [inputMessages, setInputMessages] = useState([]);
  const [inputMessagesLoading, setInputMessagesLoading] = useState(false);
  const [inputMessagesError, setInputMessagesError] = useState("");
  const [expandedMessageId, setExpandedMessageId] = useState("");
  const [messageContentCache, setMessageContentCache] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [syncState, setSyncState] = useState("idle");
  const syncPollRef = useRef(null);
  const shouldAutoRefreshActionItems = shouldAutoRefreshActionItemsForFirstRun(workflow);

  useEffect(() => {
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = String(params.get("tab") || "").trim().toLowerCase();
    if (SUPPORTED_SETTINGS_TABS.has(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!workflowId) return;
    const params = new URLSearchParams(location.search);
    const tab = String(params.get("tab") || "").trim().toLowerCase();
    const hasDeepLink =
      Boolean(params.get("action_item")) ||
      Boolean(params.get("meeting_from_label")) ||
      Boolean(params.get("file_id")) ||
      Boolean(params.get("source_snippet"));
    if (tab !== "todo" || !hasDeepLink) return;
    const next = new URLSearchParams();
    next.set("workflow", workflowId);
    ["action_item", "meeting_from_label", "file_id", "source_snippet"].forEach((key) => {
      const value = params.get(key);
      if (value) next.set(key, value);
    });
    navigate(`/business/action-items?${next.toString()}`, { replace: true });
  }, [workflowId, location.search, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const [workflows, gmailStatus] = await Promise.all([
          getBusinessWorkflows(),
          getGmailStatus(),
        ]);
        const current = workflows.find((item) => item.workflow_id === workflowId);
        if (!current) {
          setError("Workflow not found");
          return;
        }
        setWorkflow(current);
        const filterState = parseFiltersPayload(gmailStatus?.filters || current.filters || []);
        setMeetingSenderPatterns(filterState.meetingSenderPatterns);
        setMeetingSubjectKeywords(filterState.meetingSubjectKeywords);
        setWorkflowName(String(current.name || ""));
      } catch (err) {
        setError(err.message);
      }
    };
    load();
  }, [workflowId]);

  useEffect(() => {
    if (!workflowId || !shouldAutoRefreshActionItems) {
      return undefined;
    }
    const intervalId = window.setInterval(async () => {
      try {
        const workflows = await getBusinessWorkflows();
        const current = workflows.find((item) => item.workflow_id === workflowId);
        if (current) {
          setWorkflow(current);
        }
      } catch (_error) {
        // Non-blocking: keep existing workflow state if refresh fails.
      }
    }, WORKFLOW_STATUS_REFRESH_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [workflowId, shouldAutoRefreshActionItems]);

  useEffect(() => {
    if (activeTab !== "history" || !workflowId) {
      return;
    }
    const loadRuns = async () => {
      try {
        setHistoryLoading(true);
        setHistoryError("");
        const runs = await getBusinessWorkflowRuns(workflowId, 30);
        setRunHistory(Array.isArray(runs) ? runs : []);
        setSelectedRun((current) => {
          if (current) {
            const stillPresent = runs.find(
              (item) => buildRunSelectionKey(item) === buildRunSelectionKey(current)
            );
            if (stillPresent) {
              return stillPresent;
            }
          }
          return runs[0] || null;
        });
      } catch (err) {
        setHistoryError(getRunHistoryErrorMessage(err));
      } finally {
        setHistoryLoading(false);
      }
    };
    loadRuns();
  }, [activeTab, workflowId]);

  useEffect(() => {
    if (activeTab !== "history" || !workflowId || !selectedRun) {
      return;
    }
    const loadInputMessages = async () => {
      try {
        setInputMessagesLoading(true);
        setInputMessagesError("");
        setExpandedMessageId("");
        const items = await getRunInputMessages(
          workflowId,
          selectedRun.digest_payload_gcs_url
        );
        setInputMessages(Array.isArray(items) ? items : []);
      } catch (err) {
        setInputMessagesError(getInputMessagesErrorMessage(err));
        setInputMessages([]);
      } finally {
        setInputMessagesLoading(false);
      }
    };
    loadInputMessages();
  }, [activeTab, workflowId, selectedRun]);

  const handleRunSelection = (event) => {
    const selectedKey = event.target.value;
    const selected = runHistory.find(
      (run) => buildRunSelectionKey(run) === selectedKey
    );
    setSelectedRun(selected || null);
  };

  const handleToggleSourceMessage = async (sourceMessage, triggerElement) => {
    const messageId = String(sourceMessage?.message_id || "");
    if (!messageId) {
      return;
    }
    const scrollContainer = triggerElement?.closest(".business-run-right-pane");
    const previousTop = triggerElement?.getBoundingClientRect?.().top ?? 0;
    const stabilizeScrollPosition = () => {
      if (!scrollContainer || !triggerElement?.getBoundingClientRect) {
        return;
      }
      const nextTop = triggerElement.getBoundingClientRect().top;
      scrollContainer.scrollTop += nextTop - previousTop;
    };

    if (expandedMessageId === messageId) {
      setExpandedMessageId("");
      requestAnimationFrame(stabilizeScrollPosition);
      return;
    }
    setExpandedMessageId(messageId);
    requestAnimationFrame(stabilizeScrollPosition);
    const existingEntry = messageContentCache[messageId];
    if (existingEntry || !sourceMessage.gcs_url) {
      return;
    }
    setMessageContentCache((prevCache) => ({
      ...prevCache,
      [messageId]: { loading: true, error: "", content: "" },
    }));
    try {
      const response = await getRunInputMessageContent(workflowId, sourceMessage.gcs_url);
      setMessageContentCache((prevCache) => ({
        ...prevCache,
        [messageId]: {
          loading: false,
          error: "",
          content: String(response?.content || ""),
        },
      }));
    } catch (err) {
      setMessageContentCache((prevCache) => ({
        ...prevCache,
        [messageId]: {
          loading: false,
          error: String(err?.message || "Failed to load source content"),
          content: "",
        },
      }));
    }
  };

  const handleSyncNow = async () => {
    if (syncState === "syncing") return;
    setSyncState("syncing");
    if (syncPollRef.current) clearInterval(syncPollRef.current);

    let connectorJobId = null;
    let queuedAtMs = 0;
    try {
      const result = await syncWorkflowNow(workflowId);
      connectorJobId = result?.connector_job_id || null;
      queuedAtMs = result?.queued_at_ms || 0;
      // already_syncing means a live job is running — poll it, don't error out
    } catch {
      setSyncState("failed");
      return;
    }

    const deadline = Date.now() + 10 * 60 * 1000;
    syncPollRef.current = setInterval(async () => {
      try {
        const result = await getWorkflowSyncStatus(workflowId, {
          connectorJobId: connectorJobId || undefined,
          sinceMs: queuedAtMs || undefined,
        });
        const status = String(result?.status || "").toUpperCase();
        if (status === "DONE") {
          clearInterval(syncPollRef.current);
          setSyncState("done");
        } else if (status === "FAILED" || status === "DELETED") {
          clearInterval(syncPollRef.current);
          setSyncState("failed");
        } else if (Date.now() > deadline) {
          clearInterval(syncPollRef.current);
          setSyncState("idle");
        }
      } catch {
        clearInterval(syncPollRef.current);
        setSyncState("failed");
      }
    }, 4000);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const trimmedWorkflowName = String(workflowName || "").trim();
      if (trimmedWorkflowName && trimmedWorkflowName !== String(workflow?.name || "").trim()) {
        await updateBusinessWorkflow(workflowId, { name: trimmedWorkflowName });
        setWorkflow((previous) =>
          previous ? { ...previous, name: trimmedWorkflowName } : previous
        );
      }
      await updateGmailSettings({
        filters: buildSettingsGmailFiltersPayload({
          includeInviteEmails: true,
          meetingSenderPatterns,
          meetingSubjectKeywords,
        }),
        schedule_timezone: getBrowserTimezone(),
      });
      setMessage("Settings updated");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this workflow?"
    );
    if (!shouldDelete) {
      return;
    }
    try {
      setError("");
      await deleteBusinessWorkflow(workflowId);
      navigate("/business/settings?settingsTab=connect-apps");
    } catch (err) {
      setError(err.message);
    }
  };

  if (!workflow) {
    return (
      <BusinessPageLoader text={error || "Loading workflow settings…"} />
    );
  }

  const workflowStatus = getWorkflowLifecycleStatus(workflow);
  const suppressHistoryError =
    Number(workflow?.next_run_time || 0) > 0 &&
    historyError === FIRST_RUN_HISTORY_PENDING_MESSAGE;

  return (
    <div
      className={`business-wizard-wrap business-wizard-wrap--workflow-settings ${
        activeTab === "history" || activeTab === "todo" ? "business-wizard-wrap--history" : ""
      }`}
    >
      <div className="business-settings-header">
        <div className="business-wizard-header">
          <button
            className="business-wizard-back-btn"
            onClick={() => navigate("/business/settings?settingsTab=connect-apps")}
            type="button"
            aria-label="Go back to connected apps"
          >
            <IoArrowBack />
          </button>
          <div className="business-wizard-header-content">
            <h2 className="business-wizard-title">{workflow.name}</h2>
          </div>
        </div>
        {activeTab === "todo" ? (
          <div className="business-settings-header-actions workflow-action-summary__top-actions">
            <button
              type="button"
              className="workflow-action-summary__btn workflow-action-summary__btn--ghost"
              onClick={() => setActiveTab("settings")}
            >
              Workflow settings
            </button>
            <Link
              className="workflow-action-summary__btn workflow-action-summary__btn--primary"
              to={`/business/action-items?workflow=${encodeURIComponent(workflowId)}`}
            >
              Action items
            </Link>
          </div>
        ) : null}
        {activeTab === "settings" ? (
          <div className="business-settings-header-actions workflow-action-summary__top-actions">
            <button
              type="button"
              className="workflow-action-summary__btn workflow-action-summary__btn--ghost"
              onClick={() => setActiveTab("todo")}
            >
              Action items overview
            </button>
            <button
              type="button"
              className="workflow-action-summary__btn workflow-action-summary__btn--ghost"
              onClick={handleSyncNow}
              disabled={syncState === "syncing"}
            >
              {syncState === "syncing"
                ? "Syncing…"
                : syncState === "done"
                ? "Sync done"
                : syncState === "failed"
                ? "Sync failed"
                : "Sync now"}
            </button>
            <button
              type="button"
              className="workflow-action-summary__btn workflow-action-summary__btn--danger"
              onClick={handleDelete}
            >
              Delete workflow
            </button>
          </div>
        ) : null}
      </div>
      <div className="business-settings-content">
        {activeTab === "todo" ? (
          <BusinessWorkflowActionItemsSummary
            workflow={workflow}
            isInitialRunAutoRefreshing={shouldAutoRefreshActionItems}
          />
        ) : activeTab === "settings" ? (
          <>
            <h3>Email filters</h3>
            <p className="business-helper-text">
              Email filters directly control digest quality. Only emails that match these filters are
              stored and considered for summaries, action items, and priorities.
            </p>
            <GmailFilterSetupCard
              senderPatterns={meetingSenderPatterns}
              subjectKeywords={meetingSubjectKeywords}
              onSenderPatternsChange={setMeetingSenderPatterns}
              onSubjectKeywordsChange={setMeetingSubjectKeywords}
            />
            <div className="form-input-group business-filter-field">
              <label className="form-label">Workflow name</label>
              <input
                className="form-control"
                value={workflowName}
                onChange={(event) => setWorkflowName(event.target.value)}
                placeholder="Rename this workflow"
              />
            </div>
          </>
        ) : (
          <>
            {workflowStatus ? (
              <div className="business-settings-history-status-wrap">
                <div className="business-settings-header-status">
                  <span className="business-settings-header-status__label">{workflowStatus.primary}</span>
                  {workflowStatus.secondary ? (
                    <span className="business-settings-header-status__meta">
                      {workflowStatus.secondary}
                    </span>
                  ) : null}
                  {workflowStatus.showProgress ? (
                    <div className="business-workflow-status-progress" aria-hidden="true">
                      <div
                        className="business-workflow-status-progress__fill"
                        style={{
                          width: `${Math.min(Math.max(workflowStatus.progressPercent, 0), 100)}%`,
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
            <BusinessWorkflowRunHistory
              historyLoading={historyLoading}
              historyError={historyError}
              suppressHistoryError={suppressHistoryError}
              runHistory={runHistory}
              buildRunSelectionKey={buildRunSelectionKey}
              selectedRun={selectedRun}
              onRunSelection={handleRunSelection}
              inputMessagesLoading={inputMessagesLoading}
              inputMessagesError={inputMessagesError}
              inputMessages={inputMessages}
              expandedMessageId={expandedMessageId}
              messageContentCache={messageContentCache}
              onToggleSourceMessage={handleToggleSourceMessage}
            />
          </>
        )}
      </div>
      {message ? <div className="success-message">{message}</div> : null}
      {error ? <div className="error-message">{error}</div> : null}
      {activeTab === "settings" ? (
        <div className="business-actions-row business-actions-row--single">
          <button
            type="button"
            className="workflow-action-summary__btn workflow-action-summary__btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default BusinessWorkflowSettings;
